import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  confirmTwoFactor,
  disableTwoFactor,
  getTwoFactorStatus,
  regenerateBackupCodes,
  setupTwoFactor,
} from '../services/twoFactorService';

const TwoFactorSettings: React.FC = () => {
  const [status, setStatus] = useState<{ twoFactorEnabled: boolean; twoFactorMandatory: boolean; backupCodesRemaining: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await getTwoFactorStatus();
      setStatus(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load two-factor status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const startSetup = async () => {
    setError(null);
    setActionLoading(true);
    try {
      const data = await setupTwoFactor();
      setQrCodeDataUrl(data.qrCodeDataUrl);
      setManualEntryKey(data.manualEntryKey);
      setSetupOpen(true);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to start setup');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmSetup = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await confirmTwoFactor(confirmCode);
      setSetupOpen(false);
      setConfirmCode('');
      if (result.backupCodes?.length) {
        setBackupCodes(result.backupCodes);
      }
      setSuccess('Two-factor authentication enabled');
      await loadStatus();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Invalid verification code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    setActionLoading(true);
    setError(null);
    try {
      await disableTwoFactor(password, confirmCode);
      setDisableOpen(false);
      setPassword('');
      setConfirmCode('');
      setSuccess('Two-factor authentication disabled');
      await loadStatus();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to disable two-factor authentication');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const codes = await regenerateBackupCodes(password, confirmCode);
      setRegenerateOpen(false);
      setPassword('');
      setConfirmCode('');
      setBackupCodes(codes);
      await loadStatus();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to regenerate backup codes');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return null;

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>Two-factor authentication</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Use an authenticator app for an extra layer of security when signing in.
        {status?.twoFactorMandatory && ' Required for administrator accounts.'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {status?.twoFactorEnabled ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>Authenticator app is enabled</Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Backup codes remaining: {status.backupCodesRemaining}
          </Typography>
          <Button variant="outlined" sx={{ mr: 1 }} onClick={() => setRegenerateOpen(true)}>
            Regenerate backup codes
          </Button>
          {!status.twoFactorMandatory && (
            <Button variant="outlined" color="error" onClick={() => setDisableOpen(true)}>
              Disable
            </Button>
          )}
        </Box>
      ) : (
        <Button variant="contained" onClick={startSetup} disabled={actionLoading}>
          Set up authenticator
        </Button>
      )}

      <Dialog open={setupOpen} onClose={() => setSetupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set up authenticator</DialogTitle>
        <DialogContent>
          {qrCodeDataUrl && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <img src={qrCodeDataUrl} alt="QR code" style={{ maxWidth: 180 }} />
              <Typography variant="caption" display="block" sx={{ mt: 1, wordBreak: 'break-all' }}>
                Manual key: {manualEntryKey}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Verification code"
            value={confirmCode}
            onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmSetup} disabled={actionLoading || confirmCode.length !== 6}>
            Enable
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={disableOpen} onClose={() => setDisableOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable two-factor authentication</DialogTitle>
        <DialogContent>
          <TextField fullWidth type="password" label="Password" value={password} onChange={(e) => setPassword(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Authenticator code" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDisable} disabled={actionLoading}>Disable</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={regenerateOpen} onClose={() => setRegenerateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Regenerate backup codes</DialogTitle>
        <DialogContent>
          <TextField fullWidth type="password" label="Password" value={password} onChange={(e) => setPassword(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Authenticator code" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRegenerate} disabled={actionLoading}>Regenerate</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(backupCodes)} onClose={() => setBackupCodes(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Your backup codes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Save these codes securely. Each can only be used once.</Alert>
          <Box sx={{ fontFamily: 'monospace' }}>
            {backupCodes?.map((c) => <Typography key={c}>{c}</Typography>)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setBackupCodes(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TwoFactorSettings;
